// Base libs
import { useEffect, useState, useRef } from "react";

// Mantine UI libs
import { useForm } from "@mantine/form";
import {
  Alert,
  Anchor,
  Box,
  Button, Card,
  Checkbox,
  Container,
  Grid,
  Group,
  Image,
  LoadingOverlay,
  PasswordInput,
  Stack,
  Text,
  TextInput,
  useMantineTheme
} from '@mantine/core';
import SignUpSlider from '../components/SignUpSlider';
import VisibilityIcon from '@material-ui/icons/Visibility';
import VisibilityOffIcon from '@material-ui/icons/VisibilityOff';

// Configs etc
import { config } from "../neoverv/config/Setting";
import APIClient from "../neoverv/config/APIClient";
import { WarningRounded } from "@material-ui/icons";
import { showNotification } from "@mantine/notifications";
import RedCross from '../assets/images/module/general/RedCross.svg';
import GreenTick from '../assets/images/module/general/GreenTick.svg';
import Autoload from "../neoverv/Autoload";
import classes from './SignUp.module.css';
import { getPasswordStrength as getStrength } from '../helpers/passwordValidation';
import PasswordGuidanceInput from '../components/PasswordGuidanceInput';
import { LEGAL_DOCUMENT_LINKS } from '../helpers/legalConsent';


/**
 * SignUp component markup
 * @returns {JSX.Element}
 * @constructor
 */
const SignUp = (props) => {
  const theme = useMantineTheme(); // <-- Add this here
  const unknownErrorString = 'Es ist ein technischer Fehler aufgetreten. Der hellohousing Service wurde informiert. Bitte haben Sie einige Minuten Geduld und wiederholen Sie dann den Vorgang.' //'Es ist ein unerwarteter Fehler aufgetreten.'; // TODO: move it to translations file
  const [loading, setLoading] = useState(false);
  const [apiError, setApiError] = useState(null);
  const [success, setSuccess] = useState(null);
  const [emailValidateSuccess, setEmailValidateSuccess] = useState(null);
  const [passcode, setPasscode] = useState('');

  const [requiresChallenge, setRequiresChallenge] = useState(false);
  const challengeContainerRef = useRef(null);
  const recaptchaWidgetRef = useRef(null);

  const nebenkostenabrechnung = window.location.href.includes('/nebenkostenabrechnung');
  const awc = Autoload.Utils.getUrlParameterByName('awc') || null;
  console.log('awc: ', awc);
  const form = useForm({
    initialValues: {
      email: '',
      password: '',
      terms: false,
      devPassword: ''
    },
    validate: {
      email: (value) => (/^\S+@\S+$/.test(value) ? null : 'Ungültige e-mail')
    }
  });
  const strength = getStrength(form.getInputProps('password').value);

  useEffect(() => {    
    //window.Intercom('update');
    window.Intercom('boot', {  
      app_id: 'vvrs5bx3',
      Environment: window.location.hostname.includes('app') ? 'Production' : 'Staging',
    });
    console.log('this.props.history: ', document.referrer);
  }, []);
  // Send data to HubSpot
  // const sendDataToHubspot = async () => {
  //   await hubspotClient.crm.contacts.basicApi.create({
  //     email: form.getInputProps('email').value
  //   })
  //     .then((response) => {
  //       console.log("HUBSPOT RESPONSE: ", response);
  //     })
  //     .catch((err) => {
  //       console.log("HUBSPOT ERROR: ", err);
  //     });
  // }

  const showGeneralError=()=>{
    showNotification({
      message: 'Es ist ein Fehler aufgetreten. Bitte probieren Sie es erneut oder wenden Sie sich an den Support.',
      icon: <img src={RedCross} alt='red-cross'/>,
      id: 'generalSignUpError'
    })
  }

  const verifyEmail = async () =>{
    setLoading(true);
    await APIClient({
      method: 'POST',
      url: config.signUpVerifyEmail,
      data: {
        email: form.getInputProps('email').value,
        passwordResetCode: passcode
      }
    }). then((response)=>{
        setEmailValidateSuccess(true);
        setSuccess(false);
        setLoading(false);
    }) .catch((e)=>{
      // setApiError(unknownErrorString);
      showGeneralError();
      var metadata = {
        error: e.toString(),
        'end point name': config.signUpVerifyEmail
      };
      window.Intercom('trackEvent', 'sign-up-failed', metadata);
      setLoading(false);

      showNotification({
        icon: <img src={RedCross} alt='red-tick'/>,
        message: 'Passcode ist nicht gültig.'
      })
    })
  }
  
  // Activate account
  const activateAccount = async (accountId) => {
    await APIClient({
      method: 'PUT',
      url: config.signUpActivateAccount,
      data: {
        accountId: accountId
      }
    })
      .then(async(response) => {
        // sendDataToHubspot();
        // Note: the combined checkbox above also covers AVV, but activateAccount
        // only stamps GeneralTerms/DataPolicy. DataProcessing consent is left
        // unset here on purpose — the new account's first authenticated app
        // load surfaces LegalConsentModal, which records it via the generic
        // consents endpoint (that endpoint derives the account from the auth
        // token, so it can't be called pre-login during signup).

        setTimeout(() => {
          if(response.status === 200 && response.data.status){
            window.Intercom('trackEvent', 'signed-up');
          }
          setSuccess(true);
          setLoading(false);
        }, 3000);
        
        // TODO: Uncomment to activate email verification
        // await APIClient({
        //   method: 'POST',
        //   url: config.signUpEmailVerificationInstructions,
        //   data: {
        //     email: form.getInputProps('email').value
        //   }
        // }). then((response)=>{
        //   console.log('signUpEmailVerificationInstructions: ', response);
        //   setSuccess(true);
        //   setLoading(false);
        // }) .catch((e)=>{
        //   setApiError(unknownErrorString);
        //   showGeneralError();
        //   var metadata = {
        //     error: e.toString(),
        //     'end point name': config.signUpEmailVerificationInstructions
        //   };
        //   window.Intercom('trackEvent', 'sign-up-failed', metadata);
        //   setLoading(false);
        // })
      })
      .catch((e) => {
        //setApiError(unknownErrorString);
        showGeneralError();
        var metadata = {
          error: e.toString(),
          'end point name': config.signUpActivateAccount
        };
        window.Intercom('trackEvent', 'sign-up-failed', metadata);
        setLoading(false);
      });
  }
  
  // Set user subscription
  const setSubscription = async (accountId) => {
    await APIClient({
      method: 'PUT',
      url: config.signUpSubscription,
      data: {
        accountId: accountId,
        subscriptionType: "32bccff3-28b3-453a-a6ad-4023ebb8a734"
      }
    })
      .then(() => {
        activateAccount(accountId);
        
        //props.history.push(`${window.location.pathname}&event=starter`);
        // window.dataLayer.push({ 
        //   event: 'awinStarter',
        //   apiData: null
        // });
      })
      .catch((e) => {
        //setApiError(unknownErrorString);
        showGeneralError();
        var metadata = {
          error: e.toString(),
          'end point name': config.signUpSubscription
        };
        window.Intercom('trackEvent', 'sign-up-failed', metadata);
        setLoading(false);
      }); 
  }
  
  // Put user data into freshly created account
  const putUserData = async (accountId, userId, password) => {
    
    await APIClient({
      method: 'PUT',
      url: config.signUpAccountDetails,
      data: {
        accountId: accountId, // proper ID from auth-api/signUp/account response. b8eec9e9-3f71-4f08-f58e-08d9b4156a4b in my case
        password: password, // password from form
        firstName: form.getInputProps('email').value.split('@')[0], // Set user name from email prefix
        company: '',
        address: '',
        city: '',
        zipCode: ''
      }
    })
      .then(async (response) => {

        if (response.status === 200) {
          console.log('putUserData: ', response);
          await window.Intercom("update", {
            email: form.getInputProps('email').value, // Email address,
            Environment: window.location.hostname.includes('app') ? 'Production' : 'Staging',
            'Number of properties': (0).toString(),
            'Number of condominiums': (0).toString(),
            'Rental Unit Created': false,
            'Tenant Created': false,
            'Receipt Created': false,
            'Rental Unit Created': false,
            'Receipt Created Automated': false,
            'Meter Reading Conducted': false,
            'Company': '',
            'Meter Created': false,
            'Utility Billing Created': false,
            'Account Deleted': false,
            'Account Id': accountId,
            user_id: userId,
            awc: awc
          });
          //setSubscription(accountId); //TODO: activateAccount(accountId)
          activateAccount(accountId);
        } else {
          //setApiError(unknownErrorString);
          showGeneralError();
          setLoading(false);
        }
        
      })
      .catch((e) => {
        //setApiError(unknownErrorString);
        showGeneralError();
        var metadata = {
          error: e.toString(),
          'end point name': config.signUpAccountDetails
        };
        window.Intercom('trackEvent', 'sign-up-failed', metadata);
        setLoading(false);
      });
  }
  
  // Handle form submission
  const handleFormSubmit = async (values, fallbackToken = null, isRecaptchaV2=false) => {
    if (loading) return;

    if(values.password.length !== 0 && ((window.location.href.includes('dev') || window.location.href.includes('localhost')) ? values.devPassword === 'Testing9!' : true)) {
      if(strength === 100){
        setLoading(true);
    
        // 1. GENERATE RECAPTCHA TOKEN
        let recaptchaToken = fallbackToken;
        // If we don't have a fallback token yet, try the invisible check
        if (!recaptchaToken) {
          try {
            await new Promise((resolve) => window.grecaptcha.enterprise.ready(resolve));
            recaptchaToken = await window.grecaptcha.enterprise.execute('6Lee-40sAAAAAH3KM7HK3E9rq13H69XWcptUYE2a', { action: 'signup' });
          } catch (error) {
            console.error('Invisible reCAPTCHA failed:', error);
            setLoading(false);
            showNotification({
              icon: <img src={RedCross} alt='red-cross'/>,
              message: 'Sicherheitsüberprüfung fehlgeschlagen. Bitte laden Sie die Seite neu.'
            });
            return;
          }
          // setRequiresChallenge(true);
          // setTimeout(() => {
          //   window.grecaptcha.enterprise.render(challengeContainerRef.current, {
          //     sitekey: '6Ldi5rYsAAAAAPMy2dv_Rxe99-WiKfE_FosVcnQN', // Use the NEW Checkbox key here
          //     callback: (solvedToken) => {
          //       // When the user solves the puzzle, automatically resubmit
          //       handleFormSubmit(values, solvedToken, true);
          //     },
          //     'error-callback': () => {
          //       showNotification({
          //         icon: <img src={RedCross} alt='red-cross'/>,
          //         message: 'Es gab ein Problem mit dem Captcha.'
          //       });
          //     }
          //   });  
          // }, 100);
          // return null;
        }

        // 2. SEND TOKEN TO BACKEND
        await APIClient({
          method: 'POST',
          url: config.signUpAccount,
          data: { 
            email: values.email,
            recaptchaToken: recaptchaToken, // Attach the token here,
            agencyReferralToken: awc,
            isRecaptchaV2
          } 
        })
          .then(async (response) => {
            
            if (response.status === 200) {
              setRequiresChallenge(false);
              recaptchaWidgetRef.current = null;
              //UET conversion reporting starts
              window.uet_report_conversion();
              //UET conversion reporting ends

              const { accountId, user } = response.data.result;
              
              putUserData(accountId, user?.userId, values.password);
              
              //Gtag events hit starts
              try {
                window.gtag('event', 'user_sign_up', {
                  'app_name': 'helloHousingApp'
                });                
              } catch (error) {
                console.error('error-gtag: user_sign_up: ', error);
              }

              try {
                window.dataLayer.push({
                  event: "consent",
                  ad_storage: "granted",
                  analytics_storage: "granted"
                });
                  
                window.dataLayer.push({
                  event: "signup_form_submission_event_2",
                  send_to: "AW-10786932522/uE4dCIK1hZsYEKqOzpco"
                });                   
                console.log("signup_form_submission_event successful! Event sent to GTM.");
              } catch (error) {
                console.error('error-gtag: signup_form_submission_event: ', error);
              }
              //Gtag events hit ends

              //OpenAI Ads conversion reporting starts
              try {
                window.oaiq('measure', 'registration_completed', {
                  type: 'customer_action',
                });
              } catch (error) {
                console.error('error-oaiq: registration_completed: ', error);
              }
              //OpenAI Ads conversion reporting ends

            } else {
              showGeneralError();
              setLoading(false)
            }
            
          })
          .catch((e) => {
            setLoading(false);
            console.log('error: ', e?.response?.data?.message);
            
            // 3. HANDLE 403 FORBIDDEN RESPONSE
            if (e?.response?.data?.message === 'FORBIDDEN') {
              setRequiresChallenge(true);
              // Wait for the UI to render the container, then inject the Checkbox
              setTimeout(() => {
                if (window.grecaptcha && challengeContainerRef.current) {
                  // Check if the widget has already been rendered
                  if (recaptchaWidgetRef.current !== null) {
                    // It exists, so we just reset it to give the user a fresh challenge
                    window.grecaptcha.enterprise.reset(recaptchaWidgetRef.current);
                  } else {
                    // It doesn't exist yet, render it for the first time and save the ID
                    recaptchaWidgetRef.current = window.grecaptcha.enterprise.render(challengeContainerRef.current, {
                      sitekey: '6LeIxAcTAAAAAJcZVRqyHh71UMIEGNQ_MXjiZKhI',//'6Ldi5rYsAAAAAPMy2dv_Rxe99-WiKfE_FosVcnQN', // Use the NEW Checkbox key here
                      callback: (solvedToken) => {
                        // When the user solves the puzzle, automatically resubmit
                        handleFormSubmit(values, solvedToken, true);
                      },
                      'error-callback': () => {
                        showNotification({
                          icon: <img src={RedCross} alt='red-cross'/>,
                          message: 'Es gab ein Problem mit dem Captcha.'
                        });
                      }
                    });
                  }
                  // // Ensure we don't render it multiple times in the same container
                  // challengeContainerRef.current.innerHTML = ''; 
                  // window.grecaptcha.enterprise.render(challengeContainerRef.current, {
                  //   sitekey: '6Ldi5rYsAAAAAPMy2dv_Rxe99-WiKfE_FosVcnQN', // Use the NEW Checkbox key here
                  //   callback: (solvedToken) => {
                  //     // When the user solves the puzzle, automatically resubmit
                  //     handleFormSubmit(values, solvedToken, true);
                  //   },
                  //   'error-callback': () => {
                  //     showNotification({
                  //       icon: <img src={RedCross} alt='red-cross'/>,
                  //       message: 'Es gab ein Problem mit dem Captcha.'
                  //     });
                  //   }
                  // });
                }
              }, 100);
            } else {
              showGeneralError();
            }

            var metadata = {
              email: values.email,
              error: e.toString(),
              'end point name': config.signUpAccount
            };
            window.Intercom('trackEvent', 'sign-up-failed', metadata);
          });
      } else {
        showNotification({
          icon: <img src={RedCross} alt='red-tick'/>,
          message: 'Bitte wählen Sie ein sicheres Passwort.'
        })
      }
    } else {
      showNotification({
        icon: <img src={RedCross} alt='red-tick'/>,
        message: 'Bitte geben Sie ein Passwort ein.'
      })
    }
  };
  console.log('window.location.href: ', window.location.href);
  // Render html
  return (
    <Container p={0} m={0} fluid bg={'white'}>
      <Grid grow>
        {/* Slider / Left Image Area */}
        <Grid.Col 
          span={4} 
          style={{backgroundColor: 'rgb(247, 247, 250)'}}
        >
          <Image 
            w={'44%'}
            pos="absolute" 
            top={100} 
            left={0} 
            src={`${process.env.PUBLIC_URL}/${nebenkostenabrechnung ? 'nebenkosten.png' : 'signup-image.png'}`} 
            alt="Nebenkosten" 
          />
        </Grid.Col>

        {/* Form Area */}
        <Grid.Col 
          span={8} // <-- v7 Syntax for Grid sizing
          bg="white"
          h={'100vh'}
        >
          <Stack align="center" justify="center" gap={48} h={'inherit'}>
            <Stack gap={36} style={{ marginBottom: 56, width: '100%', maxWidth: '560px' }}>
              
              <form onSubmit={form.onSubmit((values) => handleFormSubmit(values))} id={'hellohousing-signup-form'} style={{ position: 'relative' }}>
                <LoadingOverlay visible={loading} />
                
                {/* Welcome text */}
                {success === null && (
                  <Stack gap={0} style={{ marginBottom: 40 }}>
                    <Text size="lg" fw={700} style={{fontSize: '30px'}} c="#13135C">Jetzt registrieren!</Text>
                    <Text size="sm" fw={500} c="#13135C" style={{fontSize: '17px'}}>
                      {nebenkostenabrechnung ? 'Erstellen Sie Ihr Nutzerkonto, um Nebenkostenabrechnungen zu erstellen.' : 'Erstellen Sie Ihr Nutzerkonto, um ihre Immobilie mit der hellohousing WebApp zu verwalten.'}
                    </Text>
                  </Stack>
                )}

                {/* Form */}
                <Stack gap={12}>

                  {/* Display API related errors */}
                  {apiError && !success && !emailValidateSuccess && (
                    <Alert color="red" title="Fehler" icon={<WarningRounded />}>
                      {apiError}
                    </Alert>
                  )}

                  {/* Display success message */}
                  {emailValidateSuccess && ( 
                    <Card shadow="xs" p={24}>
                      <Stack gap={4}>
                        <Text size="lg" fw={700} c="#13135C">Validate Email!</Text>
                        <Text size="sm" c="#13135C">Enter 6 digit code sent to your email address.</Text>
                        <TextInput
                            label="Passcode"
                            value={passcode}
                            onChange={(e)=> setPasscode(e.target.value)}
                        />
                        <Button 
                          component="a" 
                          onClick={()=> verifyEmail()} 
                          bg="#13135C"
                          style={{ "&:hover": { color: "white", textDecoration: "none" } }} 
                          mt={24}
                        >
                          Validate
                        </Button>
                      </Stack>
                    </Card>
                  )}

                  {/* Display success message */}
                  {success && (
                    <Card shadow="xs" p={24}>
                      <Stack gap={4}>
                        <Text size="lg" fw={700} c="#13135C">Sie haben es geschafft!</Text>
                        <Text size="sm" c="#13135C">Ihr Konto wurde erstellt - jetzt können Sie sich einloggen.</Text>
                        <Button 
                          component="a" 
                          href="/login" 
                          bg="#13135C"
                          style={{ "&:hover": { color: "white", textDecoration: "none" } }} 
                          mt={24} 
                          type="submit"
                        >
                          Einloggen
                        </Button>
                      </Stack>
                    </Card>
                  )}

                  {/* Hide form on success */}
                  {success === null && (
                    <>
                      <TextInput 
                        type="email" 
                        label="E-Mail Adresse" 
                        styles={{ label: { color: "#727293" } }} 
                        required 
                        {...form.getInputProps('email')} 
                      />

                      <PasswordGuidanceInput
                        label="Passwort"
                        {...form.getInputProps('password')}
                        styles={{ label: { color: "#727293" } }}
                        visibilityToggleIcon={({ reveal, size }) =>
                          reveal ? <VisibilityOffIcon style={{ fontSize: '16px' }} /> : <VisibilityIcon style={{ fontSize: '16px' }} />
                        }
                        required
                      />

                      <PasswordInput 
                        label="Dev Passwort" 
                        {...form.getInputProps('devPassword')}
                        styles={{ label: { color: "#727293" } }}
                        visibilityToggleIcon={({ reveal, size }) =>
                          reveal ? <VisibilityOffIcon style={{ fontSize: '16px' }} /> : <VisibilityIcon style={{ fontSize: '16px' }} />
                        }
                        required 
                      />

                      <Checkbox
                        style={{ alignItems: "flex-start", "input": { marginTop: 4, background: '#F7F7FA' }, "svg": { top: 4 } }}
                        label={
                          <Text size="sm" c={'#13135C'} style={{ cursor: 'pointer', fontSize: '15px' }}>
                            Mit der Anmeldung zu hellohousing erkläre mich mit den <Anchor onClick={()=> window.open('https://hellohousing.de/agb', '_blank')}>AGB</Anchor>s, <Anchor onClick={()=> window.open(LEGAL_DOCUMENT_LINKS.dataProcessing, '_blank')}>AVV</Anchor>s und der <Anchor onClick={()=> window.open(LEGAL_DOCUMENT_LINKS.dataPolicy, '_blank')}>Datenschutzerklärung</Anchor> einverstanden.
                          </Text>
                        }
                        required
                        {...form.getInputProps('terms', { type: 'checkbox' })}
                      />
                      
                      {/* VISUAL CHALLENGE CONTAINER */}
                      {requiresChallenge && (
                        <Alert color="yellow" title="Sicherheitsüberprüfung" icon={<WarningRounded />} style={{ marginTop: 16, marginBottom: 16 }}>
                          <Text size="sm" mb={10}>Bitte bestätigen Sie, dass Sie ein Mensch sind, um fortzufahren.</Text>
                          <Box ref={challengeContainerRef} style={{ display: 'flex', justifyContent: 'center' }}></Box>
                        </Alert>
                      )}
                      
                      {!requiresChallenge && (
                        <Button 
                          className="register-hellohousing-user-nebenkostenabrechnung"
                          disabled={loading}
                          loading={loading}
                          bg="#13135C"
                          style={{width: '120px'}} 
                          type="submit"
                        >
                          Registrieren
                        </Button>
                      )}

                      <Group justify="space-between" style={{ marginTop: 24 }}>
                        <Text size="sm" style={{fontSize: '15px', fontWeight: '400'}} c="#13135C">
                          Sie haben bereits ein Konto? <Anchor href="/login" style={{fontWeight: '500'}}>Hier einloggen!</Anchor>
                        </Text>
                      </Group>
                    </>
                  )}
                </Stack>
              </form>
            </Stack>
          </Stack>
        </Grid.Col>
      </Grid>
    </Container>
  );
};

export default SignUp;